// The charge-template and charge-capture endpoints, in one place.
//
// Instance C owns the routes (backend/app/routes/charges.py); the contract is
// docs/charge-capture.md "API". Every path is practice-scoped and gated on
// `features.charge_templates`. Template writes are admin; reads are anyone who sees
// claims. Nothing here creates or submits a claim: the one claim write is
// copy-last-charge, one person's click on one draft.

const API_URL = process.env.REACT_APP_API_URL || "";

async function readError(response, fallback) {
  try {
    const body = await response.json();
    return { message: (body && body.error) || fallback, body: body || {} };
  } catch (err) {
    return { message: fallback, body: {} };
  }
}

async function request(url, options, fallback) {
  const response = await fetch(url, { credentials: "include", ...options });
  if (!response.ok) {
    const { message, body } = await readError(response, fallback);
    const error = new Error(message);
    error.status = response.status;
    error.body = body;
    throw error;
  }
  // A 200 is not a payload: an unreadable body must not render as "nothing here".
  const body = await response.json().catch(() => null);
  if (!body || typeof body !== "object") {
    const error = new Error(
      "The response could not be read, so this may be incomplete. Try again.",
    );
    error.status = response.status;
    error.body = {};
    throw error;
  }
  return body;
}

const json = (method, payload) => ({
  method,
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(payload),
});

const base = (practiceId) => `${API_URL}/api/practices/${practiceId}`;

const num = (value) => {
  const n = Number(value);
  return value == null || value === "" || !Number.isFinite(n) ? null : n;
};

export const BILL_TO = [
  { key: "insurance", label: "Insurance" },
  { key: "patient", label: "Patient" },
];

// ---- templates ----

export function normalizeTemplate(raw) {
  const t = raw || {};
  const obj = (v) => (v && typeof v === "object" && !Array.isArray(v) ? v : {});
  return {
    id: t.id,
    name: t.name || "",
    priority: num(t.priority),
    match: obj(t.match),
    lines: Array.isArray(t.lines) ? t.lines : [],
    billTo: t.bill_to || "insurance",
    payerOverrides: obj(t.payer_overrides),
    requiresNote: t.requires_note === true,
    doNotBill: t.do_not_bill === true,
    active: t.active === true,
    version: num(t.version),
    author: t.author || null,
    updatedAt: t.updated_at || null,
  };
}

export async function fetchTemplates(practiceId) {
  const body = await request(
    `${base(practiceId)}/charge-templates`,
    {},
    "Could not load the charge templates.",
  );
  if (!Array.isArray(body.templates)) {
    throw new Error(
      "The response could not be read, so this list may be incomplete. Try again.",
    );
  }
  return body.templates.map(normalizeTemplate);
}

// The body C's service validates. `version` rides along on an update so a stale save
// is refused (409) instead of overwriting someone else's edit.
export function templatePayload(t, { includeVersion = false } = {}) {
  const payload = {
    name: t.name,
    priority: t.priority,
    match: t.match,
    lines: t.lines,
    bill_to: t.billTo,
    payer_overrides: t.payerOverrides,
    requires_note: t.requiresNote,
    do_not_bill: t.doNotBill,
    active: t.active,
  };
  if (includeVersion) payload.version = t.version;
  return payload;
}

export async function createTemplate(practiceId, template) {
  const body = await request(
    `${base(practiceId)}/charge-templates`,
    json("POST", templatePayload(template)),
    "Could not create the template.",
  );
  return normalizeTemplate(body.template);
}

export async function updateTemplate(practiceId, templateId, patch) {
  const body = await request(
    `${base(practiceId)}/charge-templates/${encodeURIComponent(templateId)}`,
    json("PUT", patch),
    "Could not save the template.",
  );
  return normalizeTemplate(body.template);
}

// POST …/charge-templates/preview {appointment_id, template_id?} — writes nothing.
export async function previewVisit(
  practiceId,
  appointmentId,
  templateId = null,
) {
  const payload = { appointment_id: appointmentId };
  if (templateId != null) payload.template_id = templateId;
  const body = await request(
    `${base(practiceId)}/charge-templates/preview`,
    json("POST", payload),
    "Could not preview this visit.",
  );
  return {
    appointmentId: body.appointment_id,
    outcome: body.outcome || null,
    reason: body.reason || null,
    template: body.template ? normalizeTemplate(body.template) : null,
    facts: body.facts && typeof body.facts === "object" ? body.facts : {},
    sourceNotePresent:
      typeof body.source_note_present === "boolean"
        ? body.source_note_present
        : null,
    lines: Array.isArray(body.lines) ? body.lines : [],
    feeLookup: Array.isArray(body.fee_lookup) ? body.fee_lookup : [],
    pos: body.pos || null,
    billTo: body.bill_to || null,
    payerOverridesApplied: Array.isArray(body.payer_overrides_applied)
      ? body.payer_overrides_applied
      : [],
    tested: body.tested
      ? {
          templateId: body.tested.template_id,
          holds: body.tested.holds === true,
          reasons: Array.isArray(body.tested.reasons)
            ? body.tested.reasons
            : [],
        }
      : null,
  };
}

// GET …/charge-templates/seed-preview?preset=vortex|valetudo (C, ruling O9).
// Admin-only, read-only, writes nothing. There is NO apply route and no apply
// button: a preset goes onto a practice through
// `scripts/seed_charge_templates.py --apply --confirm`, run by a person, after
// David has read it. This is the reading.
export const SEED_PRESETS = [
  { key: "vortex", label: "Vortex (David's rules)" },
  { key: "valetudo", label: "Valetudo (from its billing map)" },
];

export async function fetchSeedPreview(practiceId, preset) {
  const body = await request(
    `${base(practiceId)}/charge-templates/seed-preview?preset=${encodeURIComponent(preset)}`,
    {},
    "Could not read the seed.",
  );
  if (!Array.isArray(body.templates)) {
    throw new Error(
      "The response could not be read, so this seed may be incomplete. Try again.",
    );
  }
  return {
    preset: body.preset || preset,
    templates: body.templates.map(normalizeTemplate),
    skippedExisting: Array.isArray(body.skipped_existing)
      ? body.skipped_existing.filter((n) => typeof n === "string")
      : [],
  };
}

// ---- charge capture ----

const normalizeVisitRow = (r) => ({
  claimId: r.claim_id ?? null,
  appointmentId: r.appointment_id ?? null,
  patientId: r.patient_id ?? null,
  patientName: r.patient_name || null,
  dateOfService: r.date_of_service || null,
  providerName: r.provider_name || null,
  reason: r.reason || null,
  flags: Array.isArray(r.flags) ? r.flags : [],
  template:
    r.template && typeof r.template === "object"
      ? { id: r.template.id, name: r.template.name || null }
      : typeof r.template === "string"
        ? { id: null, name: r.template }
        : null,
  visitKind: r.visit_kind || null,
  sourceColor: r.source_color || null,
  sourceNotePresent:
    typeof r.source_note_present === "boolean" ? r.source_note_present : null,
});

export async function fetchNeedsPerson(practiceId) {
  const body = await request(
    `${base(practiceId)}/charges/needs-person`,
    {},
    "Could not load the drafts waiting for a person.",
  );
  if (!Array.isArray(body.drafts)) {
    throw new Error(
      "The response could not be read, so this list may be incomplete. Try again.",
    );
  }
  return {
    drafts: body.drafts.map(normalizeVisitRow),
    truncated: body.truncated === true,
  };
}

export async function fetchPending(practiceId, { start, end } = {}) {
  const qs = new URLSearchParams();
  if (start) qs.set("start", start);
  if (end) qs.set("end", end);
  const suffix = qs.toString() ? `?${qs}` : "";
  const body = await request(
    `${base(practiceId)}/charges/pending${suffix}`,
    {},
    "Could not load the visits without a charge.",
  );
  if (!Array.isArray(body.visits) || !Array.isArray(body.not_billable)) {
    throw new Error(
      "The response could not be read, so this list may be incomplete. Try again.",
    );
  }
  return {
    start: body.start || null,
    end: body.end || null,
    visits: body.visits.map(normalizeVisitRow),
    notBillable: body.not_billable.map(normalizeVisitRow),
    truncated: body.truncated === true,
  };
}

// POST …/charges/<claim_id>/copy-last-charge. Replaces the draft's lines.
export async function copyLastCharge(practiceId, claimId) {
  const body = await request(
    `${base(practiceId)}/charges/${encodeURIComponent(claimId)}/copy-last-charge`,
    json("POST", {}),
    "Could not copy the last charge.",
  );
  return {
    claimId: body.claim_id,
    copiedFrom: body.copied_from
      ? {
          claimId: body.copied_from.claim_id,
          dateOfService: body.copied_from.date_of_service || null,
        }
      : null,
    lines: Array.isArray(body.lines) ? body.lines : [],
    billedAmount: num(body.billed_amount),
  };
}

// A line as a biller reads it: "99214-95 ×1 POS 02 $200.00".
// Two shapes arrive: a template line ({cpt, charge_cents | fee_schedule}) and a
// claim line from copy-last-charge ({code, charge} with charge in dollars).
export function lineLabel(line) {
  const l = line || {};
  const code = l.cpt || l.code || "?";
  const mods =
    Array.isArray(l.modifiers) && l.modifiers.length
      ? `-${l.modifiers.join("-")}`
      : "";
  const units = l.units != null ? ` ×${l.units}` : "";
  const pos = l.pos ? ` POS ${l.pos}` : "";
  let price = "";
  if (num(l.charge_cents) != null) price = ` $${(num(l.charge_cents) / 100).toFixed(2)}`;
  else if (num(l.charge) != null) price = ` $${num(l.charge).toFixed(2)}`;
  else if (l.cpt) price = " (fee schedule)";
  else price = " (unpriced)";
  return `${code}${mods}${units}${pos}${price}`;
}
